const STATUS_LABELS = new Map([
  ['succeeded', 'completed'],
  ['completed', 'completed'],
  ['partial', 'partial'],
  ['blocked', 'blocked'],
  ['failed', 'failed'],
  ['skipped', 'skipped'],
  ['running', 'running'],
  ['waiting_user', 'waiting_user'],
]);
const PROCESS_SCOPE_BY_NODE_KIND = new Map([
  ['policy', 'system'],
  ['llm', 'business'],
  ['structured_reasoning', 'business'],
  ['capability', 'business'],
  ['workflow', 'business'],
]);
const publicProjectedLabel = (value) => {
  const label = String(value || '').trim();
  return /^[a-z0-9_:-]+$/i.test(label) ? '' : label;
};
const TRACE_LABEL_FALLBACK_BY_NODE_KIND = new Map([
  ['llm', () => ''],
]);

const normalizeObject = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);
const listCount = (value) => (Array.isArray(value) ? value.length : null);
const countDetail = (label, value) => (
  Number.isInteger(value) ? `${label} ${value}` : ''
);
const itemCountDetail = (label, value) => (
  Number.isInteger(value) ? `${label} ${value} 项` : ''
);
const actionDetail = (metadata, labels) => labels.get(String(metadata.resolution_action || '').trim()) || '';

const requestScopeDetails = (metadata) => [actionDetail(metadata, new Map([
  ['execute', '本次请求范围已接纳'],
  ['reuse', '复用已接纳的请求范围'],
  ['skip', '本轮无需解析请求范围'],
  ['blocked', '请求范围未接纳'],
]))].filter(Boolean);

const identityDetails = (metadata) => {
  const scopeCount = listCount(metadata.allowed_scopes);
  return [[
    actionDetail(metadata, new Map([
      ['execute', '本次请求权限已校验'],
      ['skip', '本轮按配置跳过身份与权限校验'],
      ['blocked', '本次请求权限未通过'],
    ])),
    Number.isInteger(scopeCount) ? `允许 ${scopeCount} 项作用域` : '',
  ].filter(Boolean).join(' · ')].filter(Boolean);
};

const capabilityDetails = (metadata) => {
  const count = listCount(metadata.available_capability_ids);
  return [Number.isInteger(count) ? `当前实例可用能力 ${count} 项` : ''].filter(Boolean);
};

const contextDetails = (metadata) => {
  const inputDetail = metadata.has_current_input === true ? '本轮输入已接纳' : '';
  const materialDetail = [
    countDetail('附件', metadata.attachment_count),
    countDetail('上下文引用', metadata.context_ref_count),
    countDetail('知识引用', metadata.knowledge_ref_count),
  ].filter(Boolean).join(' · ');
  return [
    actionDetail(metadata, new Map([
      ['execute', '接纳本轮上下文与资料'],
      ['reuse', '复用已有会话上下文'],
      ['skip', '本轮无新增上下文或资料'],
      ['blocked', '上下文与资料未接纳'],
    ])),
    [inputDetail, materialDetail].filter(Boolean).join(' · '),
  ].filter(Boolean);
};

const intentDetails = (metadata) => [actionDetail(metadata, new Map([
  ['execute', '本轮任务已识别'],
  ['skip', '本轮没有可识别输入'],
  ['blocked', '本轮任务未能识别'],
]))].filter(Boolean);

const domainConfigDetails = (metadata) => (
  Object.keys(normalizeObject(metadata.domain_config_resolution)).length > 0
    ? ['当前实例配置已接纳']
    : []
);

const readinessDetails = (metadata) => {
  const readiness = normalizeObject(metadata.track_readiness);
  const workflowEntry = normalizeObject(metadata.workflow_entry);
  const blockingCount = listCount(readiness.blocking_gaps);
  const nonBlockingCount = listCount(readiness.non_blocking_gaps);
  const gapDetail = [
    Number.isInteger(blockingCount) && blockingCount > 0 ? `阻塞缺口 ${blockingCount} 项` : '',
    Number.isInteger(nonBlockingCount) && nonBlockingCount > 0 ? `非阻塞缺口 ${nonBlockingCount} 项` : '',
  ].filter(Boolean).join(' · ');
  const workflowDetail = [
    workflowEntry.recommended === true ? '深度执行可推荐' : '',
    workflowEntry.authorized === true
      ? '已授权开启'
      : workflowEntry.authorized === false ? '尚未授权开启' : '',
  ].filter(Boolean).join(' · ');
  return [gapDetail, workflowDetail].filter(Boolean);
};

const normalizedResultDetails = (nodeResult) => {
  const counts = [
    itemCountDetail('确认字段', Object.keys(normalizeObject(nodeResult.confirmed_fields)).length),
    itemCountDetail('候选字段', Object.keys(normalizeObject(nodeResult.candidate_fields)).length),
    itemCountDetail('待确认问题', listCount(nodeResult.questions)),
    itemCountDetail('结构更新', listCount(nodeResult.patches)),
  ].filter((detail) => detail && !detail.endsWith(' 0 项'));
  if (counts.length > 0) {
    return [counts.join(' · ')];
  }
  return [
    nodeResult.status === 'succeeded' ? '标准化结果已接纳' : '',
    nodeResult.status === 'partial' ? '已接纳部分结果' : '',
    nodeResult.status === 'failed' ? '执行失败' : '',
  ].filter(Boolean);
};

const DETAIL_PROJECTORS = new Map([
  ['request_scope_admission', requestScopeDetails],
  ['identity_permission_admission', identityDetails],
  ['capability_availability_admission', capabilityDetails],
  ['context_material_admission', contextDetails],
  ['intent_task_recognition', intentDetails],
  ['domain_config_admission', domainConfigDetails],
  ['current_stage_readiness', readinessDetails],
]);

function resolveProcessLabel(trace, nodeKind, metadata) {
  const explicitPublicLabel = [
    trace.visible_label,
    trace.visibleLabel,
    trace.title,
    metadata.visible_label,
    metadata.public_label,
  ].map(publicProjectedLabel).find(Boolean) || '';
  if (explicitPublicLabel) return explicitPublicLabel;
  const fallback = TRACE_LABEL_FALLBACK_BY_NODE_KIND.get(nodeKind) ?? publicProjectedLabel;
  return fallback(trace.label);
}

function resolveProcessDetails(nodeKey, nodeResult) {
  const projector = DETAIL_PROJECTORS.get(nodeKey);
  return projector
    ? projector(normalizeObject(nodeResult.metadata))
    : normalizedResultDetails(nodeResult);
}

export function buildNodeResultProcessLine(trace = {}) {
  const nodeResult = normalizeObject(trace.node_result);
  if (Object.keys(nodeResult).length === 0) {
    return '';
  }
  const nodeKey = String(nodeResult.node_key || '').trim();
  const metadata = normalizeObject(nodeResult.metadata);
  const label = resolveProcessLabel(trace, nodeResult.node_kind, metadata);
  const status = STATUS_LABELS.get(String(nodeResult.status || trace.status || '').trim());
  if (!label || !status) {
    return '';
  }
  const details = resolveProcessDetails(nodeKey, nodeResult);
  return [`[${status}] ${label}`, ...details].join('\n');
}

export function resolveNodeResultProcessVisibility(trace = {}) {
  const line = buildNodeResultProcessLine(trace);
  return line && PROCESS_SCOPE_BY_NODE_KIND.get(normalizeObject(trace.node_result).node_kind) === 'business' ? 'visible' : 'hidden';
}

export function buildNodeResultProcessEntry(trace = {}) {
  const line = buildNodeResultProcessLine(trace);
  const nodeKind = String(normalizeObject(trace.node_result).node_kind || '').trim();
  return {
    line,
    scope: line ? PROCESS_SCOPE_BY_NODE_KIND.get(nodeKind) ?? '' : '',
  };
}
